/* eslint-disable */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  UserPlus,
  ScrollText,
  Landmark,
  ShieldAlert,
  Wrench,
  DoorOpen,
  Flag,
  Building2,
  Crown,
  CalendarDays,
  ArrowRight,
  Lock,
  ThumbsUp,
} from "lucide-react";
import { apiFetch } from "../auth/apiClient";
import { boardApi } from "../board/api";
import "./AdminHome.css";

// Length of whatever array shape an endpoint hands back.
function countOf(v) {
  if (Array.isArray(v)) return v.length;
  if (v && Array.isArray(v.items)) return v.items.length;
  if (v && Array.isArray(v.requests))
    return v.requests.filter((r) => r.status === "PENDING").length;
  return 0;
}

// Resilient GET — a 403/500 for an inapplicable role resolves to a fallback
// instead of breaking the whole dashboard.
async function safe(path, fallback = null) {
  try {
    return await apiFetch(path, { authRequired: true });
  } catch (_) {
    return fallback;
  }
}

export default function AdminHome({ user }) {
  const [s, setS] = useState(null);

  const load = useCallback(async () => {
    const [
      gov,
      approvals,
      docs,
      sos,
      visitors,
      tickets,
      reports,
      teamApps,
      board,
      elders,
      props,
      issues,
      meetings,
    ] = await Promise.all([
      safe("/api/governance/overview", {}),
      safe("/api/approvals/pending", []),
      safe("/api/documents/pending-count", { count: 0 }),
      safe("/api/sos?status=ACTIVE,ACKNOWLEDGED", []),
      safe("/api/visitors?status=PENDING", []),
      safe("/api/tickets?status=OPEN,IN_PROGRESS", { items: [] }),
      safe("/api/discussions/reports?status=OPEN", { items: [] }),
      boardApi.listPendingApplications().catch(() => []),
      safe("/api/board/members", []),
      safe("/api/board/elders", []),
      safe("/api/properties?status=ACTIVE", { items: [] }),
      safe("/api/governance/issues?status=OPEN", { items: [] }),
      safe("/api/governance/meetings", { items: [] }),
    ]);
    const issueList = Array.isArray(issues) ? issues : issues?.items || [];
    const meetingList = Array.isArray(meetings) ? meetings : meetings?.items || [];
    const nextMeeting = meetingList
      .filter((m) =>
        ["NOTICE_ISSUED", "RESCHEDULED", "IN_PROGRESS"].includes(m.status)
      )
      .sort(
        (a, b) =>
          new Date(a.scheduled_at || a.meeting_at || a.date || 0) -
          new Date(b.scheduled_at || b.meeting_at || b.date || 0)
      )[0] || null;
    setS({
      members: gov?.eligible_members ?? null,
      openIssues: gov?.open_issues ?? 0,
      upcomingMeetings: gov?.upcoming_meetings ?? 0,
      lock: gov?.lock || null,
      pendingApprovals: countOf(approvals),
      docsPending: docs?.count ?? 0,
      openSos: countOf(sos),
      pendingVisitors: countOf(visitors),
      openTickets: countOf(tickets),
      reports: countOf(reports),
      teamApps: countOf(teamApps),
      boardCount: countOf(board),
      eldersCount: countOf(elders),
      listings: countOf(props),
      openIssuesList: issueList.slice(0, 4),
      nextMeeting,
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const first = (user?.name || "").trim().split(" ")[0] || "there";

  // Attention items — only those with a non-zero actionable count.
  const attention = s
    ? [
        { n: s.pendingApprovals, label: "resident approvals waiting", to: "/residents", icon: UserPlus },
        { n: s.docsPending, label: "documents awaiting approval", to: "/documents", icon: ScrollText },
        { n: s.teamApps, label: "volunteer-team applications", to: "/board", icon: Crown },
        { n: s.reports, label: "discussion reports to review", to: "/discussions/reports", icon: Flag },
        { n: s.openSos, label: "active SOS alerts", to: "/security", icon: ShieldAlert, urgent: true },
        { n: s.pendingVisitors, label: "visitors pending approval", to: "/security", icon: DoorOpen },
      ].filter((x) => x.n > 0)
    : [];

  // KPI cards — always shown; tinted when they need attention.
  const kpis = s
    ? [
        { key: "residents", value: s.members, label: "Residents", to: "/residents", Icon: Users, color: "#4F46E5" },
        { key: "issues", value: s.openIssues, label: "Open issues", to: "/governance", Icon: Landmark, color: "#D97706", warn: s.openIssues > 0 },
        { key: "tickets", value: s.openTickets, label: "Open tickets", to: "/operations", Icon: Wrench, color: "#0284C7", warn: s.openTickets > 0 },
        { key: "sos", value: s.openSos, label: "Active SOS", to: "/security", Icon: ShieldAlert, color: "#DC2626", warn: s.openSos > 0 },
        { key: "docs", value: s.docsPending, label: "Docs to approve", to: "/documents", Icon: ScrollText, color: "#7C3AED", warn: s.docsPending > 0 },
        { key: "board", value: `${s.boardCount}/8`, label: "Board seated", to: "/board", Icon: Crown, color: "#B45309" },
        { key: "meetings", value: s.upcomingMeetings, label: "Upcoming GBMs", to: "/governance", Icon: CalendarDays, color: "#2E7D32" },
        { key: "listings", value: s.listings, label: "Active listings", to: "/properties", Icon: Building2, color: "var(--accent)" },
      ]
    : [];

  return (
    <div className="vsah">
      <div className="vsah-head">
        <div>
          <h1>Welcome back, {first}</h1>
          <p className="vsah-sub">
            A snapshot of everything that needs your attention across Vishal
            Sanjivini today.
          </p>
        </div>
      </div>

      {s?.lock?.active && (
        <Link to="/governance" className="vsah-lock">
          <Lock size={16} />
          <span>
            <strong>The community is under a governance lock.</strong>{" "}
            {s.lock.reason || "A no-confidence motion is in progress."}
          </span>
          <ArrowRight size={15} />
        </Link>
      )}

      {/* KPI grid */}
      <div className="vsah-kpis">
        {!s
          ? Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="vsah-kpi vsah-kpi-skel" />
            ))
          : kpis.map((k) => (
              <Link
                key={k.key}
                to={k.to}
                className={`vsah-kpi ${k.warn ? "warn" : ""}`}
              >
                <span
                  className="vsah-kpi-icon"
                  style={{ background: `${k.color}1a`, color: k.color }}
                >
                  <k.Icon size={18} strokeWidth={1.9} />
                </span>
                <span className="vsah-kpi-value">
                  {k.value == null ? "—" : k.value}
                </span>
                <span className="vsah-kpi-label">{k.label}</span>
              </Link>
            ))}
      </div>

      <div className="vsah-cols">
        {/* Needs attention */}
        <section className="vsah-panel">
          <div className="vsah-panel-head">
            <h2>Needs your attention</h2>
          </div>
          {s && attention.length === 0 ? (
            <div className="vsah-clear">🎉 All caught up — nothing pending.</div>
          ) : (
            <ul className="vsah-attn">
              {attention.map((a, i) => (
                <li key={i}>
                  <Link to={a.to} className={a.urgent ? "urgent" : ""}>
                    <span className="vsah-attn-icon">
                      <a.icon size={16} />
                    </span>
                    <span className="vsah-attn-count">{a.n}</span>
                    <span className="vsah-attn-label">{a.label}</span>
                    <ArrowRight size={15} className="vsah-attn-go" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

      </div>

      {/* Governance: next meeting + issues seeking support */}
      {s && (s.nextMeeting || (s.openIssuesList && s.openIssuesList.length > 0)) && (
        <section className="vsah-panel vsah-gov">
          <div className="vsah-panel-head">
            <h2>Governance</h2>
            <Link to="/governance" className="vsah-see-all">
              Open governance <ArrowRight size={13} />
            </Link>
          </div>

          {s.nextMeeting && (
            <Link
              to={`/governance/meetings/${s.nextMeeting.id}`}
              className="vsah-meeting"
            >
              <span className="vsah-meeting-icon">
                <CalendarDays size={18} />
              </span>
              <span className="vsah-meeting-body">
                <strong>
                  Next meeting: {s.nextMeeting.title || "General Body Meeting"}
                </strong>
                <small>
                  {fmtMeetingWhen(s.nextMeeting)} ·{" "}
                  {s.nextMeeting.status === "RESCHEDULED"
                    ? "Rescheduled"
                    : "Notice issued"}
                </small>
              </span>
              <ArrowRight size={15} className="vsah-attn-go" />
            </Link>
          )}

          {s.openIssuesList && s.openIssuesList.length > 0 && (
            <>
              <div className="vsah-gov-sub">Issues seeking support</div>
              <ul className="vsah-issues">
                {s.openIssuesList.map((it) => (
                  <li key={it.id}>
                    <Link to={`/governance/issues/${it.id}`}>
                      <ThumbsUp size={15} className="vsah-issue-icon" />
                      <span className="vsah-issue-title">{it.title}</span>
                      {typeof it.support_count === "number" && (
                        <span className="vsah-issue-support">
                          {it.support_count}
                          {it.support_threshold
                            ? `/${it.support_threshold}`
                            : ""}{" "}
                          support
                        </span>
                      )}
                      <ArrowRight size={14} className="vsah-attn-go" />
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
    </div>
  );
}

function fmtMeetingWhen(m) {
  const iso = m.scheduled_at || m.meeting_at || m.date;
  if (!iso) return "Date to be announced";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Date to be announced";
  return d.toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
